/**
 * WMS Timezone Utilities
 *
 * Handle date/time conversions with proper timezone handling (Issue #25).
 * Always use America/Montreal timezone as specified in CLAUDE.md
 */

const TIMEZONE = 'America/Montreal';

/**
 * Format a UTC date string for display in local timezone
 * @param utcDate - UTC date string (ISO 8601)
 * @param format - Format type: 'date', 'datetime', 'time'
 * @returns Formatted date string
 */
export function formatDate(
  utcDate: string | undefined | null,
  format: 'date' | 'datetime' | 'time' = 'date'
): string {
  if (!utcDate) {
    return '-';
  }

  try {
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) {
      return '-';
    }

    switch (format) {
      case 'date':
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          timeZone: TIMEZONE,
        }).format(date);

      case 'datetime':
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: TIMEZONE,
        }).format(date);

      case 'time':
        return new Intl.DateTimeFormat('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: TIMEZONE,
        }).format(date);

      default:
        return '-';
    }
  } catch {
    return '-';
  }
}

/**
 * Format a date for input[type="date"] element (YYYY-MM-DD)
 * Converts UTC to local date in Montreal timezone
 * @param utcDate - UTC date string
 * @returns YYYY-MM-DD string or empty string
 */
export function formatDateForInput(utcDate: string | undefined | null): string {
  if (!utcDate) {
    return '';
  }

  try {
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) {
      return '';
    }

    // Get the date in Montreal timezone
    const year = date.toLocaleString('en-US', { year: 'numeric', timeZone: TIMEZONE });
    const month = date.toLocaleString('en-US', { month: '2-digit', timeZone: TIMEZONE });
    const day = date.toLocaleString('en-US', { day: '2-digit', timeZone: TIMEZONE });

    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

/**
 * Parse a date string from input[type="date"] to UTC ISO string
 * Interprets the date as Montreal timezone
 * @param dateString - YYYY-MM-DD string from input
 * @returns ISO 8601 UTC string or undefined
 */
export function parseDateFromInput(dateString: string | undefined | null): string | undefined {
  if (!dateString || typeof dateString !== 'string') {
    return undefined;
  }

  try {
    // Parse as midnight in Montreal timezone
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) {
      return undefined;
    }

    // Use Intl.DateTimeFormat to compute the UTC offset for Montreal at this date
    // This correctly handles EST (-5) vs EDT (-4) transitions
    const montrealDate = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00Z`);
    const montrealFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    const parts = montrealFormatter.formatToParts(montrealDate);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '0';
    const montrealHour = parseInt(getPart('hour'), 10);
    // UTC hour was 12, Montreal showed montrealHour, so offset = montrealHour - 12
    const offsetHours = montrealHour - 12;

    // Midnight in Montreal = midnight UTC minus the offset
    const midnightUtcMs = Date.UTC(year, month - 1, day, 0, 0, 0) - offsetHours * 3600000;
    const date = new Date(midnightUtcMs);

    if (isNaN(date.getTime())) {
      return undefined;
    }

    return date.toISOString();
  } catch {
    return undefined;
  }
}

/**
 * Get the current date/time in Montreal timezone as ISO string
 * @returns ISO 8601 UTC string
 */
export function now(): string {
  return new Date().toISOString();
}

/**
 * Get today's date at midnight in Montreal timezone
 * @returns YYYY-MM-DD string
 */
export function today(): string {
  const date = new Date();
  const year = date.toLocaleString('en-US', { year: 'numeric', timeZone: TIMEZONE });
  const month = date.toLocaleString('en-US', { month: '2-digit', timeZone: TIMEZONE });
  const day = date.toLocaleString('en-US', { day: '2-digit', timeZone: TIMEZONE });
  return `${year}-${month}-${day}`;
}

/**
 * Check if a date is in the past (relative to Montreal timezone)
 * @param utcDate - UTC date string
 * @returns True if date is in the past
 */
export function isPast(utcDate: string | undefined | null): boolean {
  if (!utcDate) {
    return false;
  }

  try {
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) {
      return false;
    }

    return date.getTime() < Date.now();
  } catch {
    return false;
  }
}

/**
 * Check if a date is in the future (relative to Montreal timezone)
 * @param utcDate - UTC date string
 * @returns True if date is in the future
 */
export function isFuture(utcDate: string | undefined | null): boolean {
  if (!utcDate) {
    return false;
  }

  try {
    const date = new Date(utcDate);
    if (isNaN(date.getTime())) {
      return false;
    }

    return date.getTime() > Date.now();
  } catch {
    return false;
  }
}
